import '../../@types/index';
import type { PassFactory } from '../../@types/lipass';
import 'core-js/actual/object';
import 'core-js/actual/array';
import 'whatwg-fetch';
import './debug';
import { isInWujie, isLocalEnv, isProductEnv, isTestEnv } from './env';
import {
    buildQueryParams,
    getINTLUserInfo,
    getQueryParam,
    isFacebook,
    isINTLBrowser,
    isIOS,
    isInstagram,
    isSamsung,
    objectMerge,
} from './tools';
import { loadScript, waitLoading } from './loader';

import partionSwitchHtml from './partitionSwitch.html';
import { getAmsLanguageTips, getGameLanguageId, getUserGameLanguage } from './i18n';
import { isWujiePrerelease, isWujieRelease, isWujieTest } from './campApi';
import { logout } from './login';
import { onEventEmitted } from './event';

/** DEBUG 加载的文件 */
const currentScript = document.currentScript as HTMLScriptElement | null;
let hash = '';
if (currentScript) {
    // let src = currentScript.getAttribute('src');
    let src = currentScript.src;
    if (!src) hash = String(Math.floor(new Date().getTime() / 86400000) * 86400);
    else hash = encodeURIComponent(src.match(/([^/#]+)$/)?.[1] ?? '').replace(/[^\w]/g, '');
    console.info(`[Act] 当前加载的脚本文件：src = ${src}, hash = ${hash}`);
}

/** 加载 MILO 库 */
export async function loadMilo() {
    // 加载 jQuery
    // @ts-ignore
    const jQuery = await waitLoading('jQuery', () => window.$);
    if (!jQuery) {
        await loadScript(`https://events.honorofkings.com/act/milo-next/jquery-1.11.3.min.js?__t=${hash}`, () => {});
        console.info('[Act] jQuery 加载完毕');
    }
    // 加载 Milo
    const Milo = await waitLoading('Milo', () => window.Milo);
    if (!Milo) {
        // await loadScript(`js/milo.js?__t=${hash}`, () => {});
        await loadScript(`https://events.honorofkings.com/act/milo-next/milo.min.js?__t=${hash}`, () => {});
        // await loadScript(`//events.honorofkings.com/act/a20240510hwhok/js/milo.js?__t=${hash}`);
        console.info('[Act] Milo 加载完毕');
    }
}

// let defaultArea = isProductEnv() ? 8 : 88;
// let defaultPartition = isProductEnv() ? 108011 : 108001;
let [defaultArea, defaultPartition] = parseAreaPartition('0');

function parseAreaPartition(str: string) {
    let [area, partition]: [number | string, number | string] = str?.split(',') as any;
    area = Number(area);
    partition = Number(partition);
    if (!area || isNaN(area) || area < 0) area = isProductEnv() ? 8 : 88;
    if (!partition || isNaN(partition) || partition < 0) partition = isProductEnv() ? 108011 : 108001;
    return [area, partition];
}

/** 开启区服切换调试工具 */
export function enablePartitionSwitch(onChange?: () => any) {
    const hostID = '__host_partition_switch';
    if (document.getElementById(hostID)) return;

    let curAreaPartition = localStorage.getItem('__default_area_partition__');
    if (!curAreaPartition) curAreaPartition = `${defaultArea},${defaultPartition}`;
    else [defaultArea, defaultPartition] = parseAreaPartition(curAreaPartition);
    console.info('[Act] 当前默认 area =', defaultArea, ', partition =', defaultPartition);
    const partitionList = {
        '0': '自动',
        '8,108011': '正式服',
        '88,108001': '新加坡研发测试1服',
        '88,108003': '新加坡研发测试2服',
        '88,108004': '主干服',
    };
    const options: string[] = [];
    Object.entries(partitionList).every(([k, v]) => {
        options.push(`<option value="${k}"${k === curAreaPartition ? ' selected' : ''}>${v}</option>`);
        return true;
    });

    const hostDiv = document.createElement('div');
    hostDiv.id = hostID;
    document.body.append(hostDiv);

    const shadow = hostDiv.attachShadow({ mode: 'open' });
    const content = new Function('options', 'return `' + partionSwitchHtml + '`')(options) as string;
    // console.debug('[Act][enablePartitionSwitch]', content);
    const dom = document.createElement('html')!;
    dom.innerHTML = content;
    shadow.appendChild(dom);

    shadow.querySelector('select')?.addEventListener('change', function () {
        console.info('[Act] 切换默认 area 和 partition', this.value);
        [defaultArea, defaultPartition] = parseAreaPartition(this.value);
        console.info('[Act] 默认 area =', defaultArea, ', partition =', defaultPartition);
        localStorage.setItem('__default_area_partition__', `${defaultArea},${defaultPartition}`);
        setTimeout(() => {
            onChange?.();
            // (window.$wujie?.location ?? location).reload();
        }, 10);
    });
}

/** 设置全局默认的 area 和 partition */
export function setDefaultAreaPartition(area: number, partition: number) {
    [defaultArea, defaultPartition] = [area, partition];
    localStorage.setItem('__default_area_partition__', `${area},${partition}`);
    console.info('[Act] 设置默认 area 和 partition', area, partition);
}

export declare interface UserInfo {
    sOpenId: string;
    sRoleId: string;
    sArea: number;
    os: number;
    sPartition: number;
    channelid: number;
    encodeparam: string;
    nickName: string;
    headUrl: string;
    langStr: string;
    langNum: number;
}

// if (isInWujie()) {
//     // 社区综合站内嵌的界面中，需要进行适配
//     const fontSize = window.getComputedStyle(document.querySelector('html') ?? document.documentElement, null).fontSize;
//     window.parent.document.documentElement.style.fontSize = fontSize;
// }

/** 使用 JSONP 的方式实现跨域请求 */
export function corsGetJson(data: { [key: string]: any }, onSuccess: ((data: any) => any) | undefined = undefined, onFailed: ((err: any) => any) | undefined = undefined) {
    const queryString = buildQueryParams(data);
    const dom = document.createElement('script') as HTMLScriptElement;

    let timer: any;
    const cancelTimer = () => {
        // @ts-ignore
        delete window[jsonp];
        if (!timer) return;
        clearTimeout(timer);
        timer = undefined;
    };

    const jsonp = `__${new Date().getTime()}_${Math.random()}_cb`.replace(/[^\w_]/g, '');
    const callback = (data: any) => {
        cancelTimer();
        console.info('[Act] corsGetJson:', data);

        if (!data) {
            onFailed?.('empty response');
            return;
        }

        try {
            onSuccess?.(data.jData?.jData?.jData || data.jData?.jData || data.jData || data);
        } catch (err) {
            onFailed?.(err);
        }

        // @ts-ignore
        delete window[jsonp];
    };
    // @ts-ignore
    window[jsonp] = callback;
    console.info('[Act] corsGetJson: jsonp =', jsonp);

    dom.async = true;
    // dom.type = 'text/script';
    dom.src = `//webide-game.iegcom.com/ide${getQueryParam('__test') === '1' ? 'Test' : ''}/?${queryString}&jsonp=${jsonp}`;
    dom.onload = () => {
        console.info('[Act] corsGetJson: 文件已加载，准备执行回调', jsonp);
        setTimeout(() => {
            // @ts-ignore
            if (window[jsonp]) {
                console.error('[Act] corsGetJson: JSONP 回调执行失败:', jsonp);
                return;
            }
            console.info('[Act] corsGetJson: JSONP 回调已执行:', jsonp);
        }, 10);
    };
    dom.onabort = () => {
        cancelTimer();
        onFailed?.('abort');
    };
    document.body.appendChild(dom);

    timer = setTimeout(() => {
        cancelTimer();
        console.error('[Act] corsGetJson: 请求超时');
        onFailed?.('timeout');
    }, 5000);
}

/** 返回一个在指定时间 interval （秒，默认3秒）内只能调用一次 fn 的函数 */
export function onceCall(fn: (...args: any[]) => any, interval: number = 3) {
    let timer: any = undefined;
    return (...args: any[]) => {
        if (timer) {
            console.debug(`[Act][onceCall] ${interval} 秒只能调用一次`);
            return;
        }
        timer = setTimeout(() => {
            clearTimeout(timer);
            timer = undefined;
        }, interval * 1000);
        return fn(...args);
    };
}

/** 启动一个定时器，每隔一定时间 tick 执行一次回调函数，如果回调函数返回 false 则表示提前终止定时器，timeout 参数表示超时 */
export function startTimer(onTick: (isTimeout: boolean, now: number, startTime: number) => any, timeout: number = 5000, tick: number = 100) {
    timeout = Math.ceil(timeout);
    const startTime = new Date().getTime(); // 启动时间
    const targetTime = startTime + timeout; // 超时时间
    const cb = () => {
        const now = new Date().getTime();
        const isTimeout = now >= targetTime; // 是否超时
        if (isTimeout) console.warn(`[Act][startTimer] 定时器超时[timeout = ${timeout}]: ${now} - ${startTime} =`, now - startTime, (now - startTime) / 1000, '秒');
        if (onTick?.(isTimeout, now, startTime) === false) return; // 显示返回 false 表示提前终止定时器
        if (!isTimeout) setTimeout(cb, tick); // 还未等待超时，等待下一次 tick 回调
    };
    setTimeout(cb, tick);
}

export enum DeepLinkType {
    GOTO_GAME = 0,
    GOTO_PANDORA = 1,
    GOTO_MSDK = 2,
    GOTO_X5 = 4,
}

/** 跳转 DeepLink 链接
 * @param type 拉起游戏的行为：0 只拉起游戏、1 拉起到潘多拉、2 MSDK、3 X5 浏览器
 * @param data Deeplink 链接参数：只是拉起游戏不需要传值或传空，拉起潘多拉传递一个 object 对象，拉起 MSDK 或 X5 浏览器传递一个 URL 链接
 * @param success 拉起成功时需要执行的回调函数
 * @param failed 拉起失败时需要跳转的失败页面，或者需要执行的回调函数
 */
export function gotoGame(
    type: DeepLinkType,
    data: undefined | null | string | { [key: string]: any },
    success: undefined | null | (() => any),
    failed: undefined | null | string | (() => any) = undefined,
) {
    let url = '';
    if (type === DeepLinkType.GOTO_GAME) {
        console.debug('[Act][DeepLink]', '拉起游戏');
        url = 'pbpsdkhok://?gamedata=JUNMFORM_Pandora';
    } else if (type === DeepLinkType.GOTO_PANDORA) {
        const str = typeof data !== 'string' ? JSON.stringify(data) : data;
        console.debug('[Act][DeepLink]', `拉起游戏跳转到潘多拉 json = ${str}`);
        url = 'pbpsdkhok://?gamedata=JUNMFORM_Pandora_' + btoa(str);
    } else if (type === DeepLinkType.GOTO_MSDK) {
        console.debug('[Act][DeepLink]', `拉起游戏 MSDK 页面链接：${data}`);
        url = 'pbpsdkhok://?gamedata=JUMPMSDK_' + encodeURIComponent(data as string);
    } else if (type === DeepLinkType.GOTO_X5) {
        console.debug('[Act][DeepLink]', `拉起游戏 X5 页面链接：${data}`);
        url = 'pbpsdkhok://?gamedata=JUMPX5_' + encodeURIComponent(data as string);
    } else {
        throw 'Invalid DeepLink Type: ' + type;
    }

    setTimeout(() => {
        console.info('[Act][DeepLink]', `拉起游戏 DeepLink = ${url}`);
        (window.$wujie?.location ?? location).href = url;
    }, 10);

    let leftCount: any = 3;
    let timer = setInterval(() => {
        leftCount! -= 1;
        if (leftCount! > 0) return;
        leftCount = null;
        clearInterval(timer);
        timer = undefined as any;

        if (document.hidden || document.visibilityState === 'hidden') {
            console.info('[Act][DeepLink]', '已成功拉起游戏~');
            gtag('event', 'deeplink_success', { event_label: 'goto game' });
            success?.();
        } else if (failed) {
            console.info('[Act][DeepLink]', '无法在规定时间内拉起游戏~');
            gtag('event', 'deeplink_failed', { event_label: 'goto game' });
            if (typeof failed === 'function') failed();
            else (window.$wujie?.location ?? location).href = failed as string;
        }
    }, 1000);
}
// 初始化下载链接
export const downloadUrls: { android: string; ios: string; [k: string]: string } = {
    android: 'https://www.honorofkings.com/HonorOfKings_App_Android_9.2.1.14_r1598485_4101_rw.apk', // 使用官网包下载
    // android: 'https://play.google.com/store/apps/details?id=com.levelinfinite.sgameGlobal', // 使用应用商店包下载
    ios: 'https://apps.apple.com/app/id1619254071', // 使用应用商店包下载
    // samsung: 'https://apps.samsung.com/appquery/preOrderDetail.as?contentld=000006644533', // 使用三星应用商店包下载
};

/** 注册 Deeplink 按钮，点击按钮时拉起游戏并跳转到潘多拉，失败的默认行为是跳转应用商店（Android现在是直接下载官网包），可以在 onFailed 回调中修改默认行为：返回 false 表示不执行后续跳转，返回字符串调试跳转到指定的链接 */
export function registerDeeplinkButton(selectors: string, data: any, onSuccess?: () => any, onFailed?: (url: string) => any) {
    const doms = document.querySelectorAll(selectors);
    if (!doms.length) return;

    // 拉起游戏客户端成功后的回调函数
    const cbSuccess = () => {
        // 已成功拉起游戏客户端
        console.info('[Act] 成功拉起游戏客户端了~');
        onSuccess?.();
    };
    // 拉起游戏客户端失败的回调函数
    const cbFailed = () => {
        // 没能在 3 秒内拉起客户端，则直接跳转到应用商店下载页面
        console.warn('[Act] 无法拉起游戏客户端或者等待超时~');
        let url = '';
        // ios 直接跳转到应用上带你
        if (isIOS()) url = downloadUrls.ios;
        // samsung 直接跳转到三星应用商店
        else if (downloadUrls.samsung && isSamsung()) url = downloadUrls.samsung;
        // Android 统一使用官网包下载
        else url = downloadUrls.android;

        const ret = onFailed?.(url); // 返回 false 表示不跳转，返回字符串表示跳转到指定链接
        // eslint-disable-next-line prettier/prettier
        if (ret === false) return; // 返回 false 表示不跳转
        else if (ret && typeof ret === 'string') url = ret; // 返回字符串表示跳转到指定链接

        gtag('event', 'deeplink_failed_to_download', { event_label: isIOS() ? 'ios' : 'android' });
        console.warn('[Act] 似乎未能打开游戏，前往下载：', url);
        (window.$wujie?.location ?? location).href = url;
    };

    for (const dom of Array.from(doms)) {
        dom.addEventListener('click', () => {
            gtag('event', 'deeplink_click', { event_label: 'deeplink_click' });
            if (isIOS() && (isFacebook() || isInstagram())) {
                // ios fb/ins 不支持 Deeplink，直接触发失败回调
                console.warn('[Act] iOS fb/ins Deeplink 不成效~');
                gtag('event', 'deeplink_failed', { event_label: 'ios fb/ins' });
                cbFailed();
            } else {
                console.info('[Act][gotoPandora] 正在尝试拉起潘多拉……');
                gotoGame(DeepLinkType.GOTO_PANDORA, data, cbSuccess, cbFailed);
            }
        });
    }
}

let userInfo: PassFactory.UserInfo | undefined;
onEventEmitted('login', (user: PassFactory.UserInfo) => {
    userInfo = user;
});

let currMiloActId: any = undefined;
let miloLocking: boolean = false;
/** 发送 Milo 请求 */
export async function miloEmit(
    actId: string,
    flowId: string,
    sData?: Milo.EmitParams['sData'],
    onSuccess?: (data: any, ret: any) => any,
    onFailed?: Milo.EmitParams['fail'],
    loading: boolean = false,
): Promise<any | void> {
    await loadMilo();
    if (!window.Milo) {
        console.error(`[Act][initMilo] 还未加载 Milo 库，请求在 HTML 页面引入以下代码
<script src="https://events.honorofkings.com/act/milo-next/jquery-1.11.3.min.js"></script>
<script src="https://events.honorofkings.com/act/milo-next/milo.min.js"></script>`);
        return;
    }
    if (currMiloActId != actId) {
        if (miloLocking) {
            // 切换活动时，先等待上一个活动的发出请求
            await new Promise((resolve) => {
                let timer = setInterval(() => {
                    if (!miloLocking) {
                        resolve(void 0);
                        clearInterval(timer);
                        timer = undefined as any;
                    }
                }, 100);
            });
        }
        let idePrefix = 'https://webide-game.iegcom.com/ide'; // 正式环境
        if (isLocalEnv()) idePrefix = '/ideTest';
        else if (isInWujie()) {
            if (isWujieTest()) idePrefix = 'https://webide-game.iegcom.com/ideTest'; // 测试环境
        } else if (isTestEnv()) idePrefix = 'https://webide-game.iegcom.com/ideTest'; // 测试环境
        Milo.setCommonConfig({
            // @ts-ignore
            intlGameid: 29134,
            needPromiseCallback: false,
            activityApi: `${idePrefix}/page/${actId}`,
            ideApi: `${idePrefix}/`,
        });
        currMiloActId = actId;
        console.debug('[ Milo setCommonConfig ] 设置公共配置：', { idePrefix, actId });
    }
    miloLocking = true;
    // 海外HOK请求必须带上 instanceid
    const gameLanguage = getUserGameLanguage();
    const intlUser = isINTLBrowser() ? getINTLUserInfo() : undefined;
    sData = objectMerge(
        {
            instanceid: +actId.split('_')[0],
            gameid: 29134,
            sArea: defaultArea,
            sPlatId: isIOS() ? 0 : 1,
            sPartition: defaultPartition,
            sLanguage: gameLanguage.toLowerCase(),
            sAmsTips: getAmsLanguageTips(gameLanguage),
            gameLanguage: gameLanguage.toLowerCase(),
            gameLanguageId: getGameLanguageId(gameLanguage),
            sCampEnv: !isInWujie() ? (isProductEnv() ? 'Prd' : 'Test') : isWujieRelease() ? 'Prd' : isWujiePrerelease() ? 'Pre' : 'Test', // 带上 WUJIE 环境参数
        },
        {
            channelid: userInfo?.channel_info.channelId,
            os: isIOS() ? 0 : 1,
            token: userInfo?.token,
            openid: userInfo?.openid,
        },
        intlUser ?? {},
        sData ?? {},
    );

    return new Promise((resolve) => {
        try {
            Milo.emit({
                actId,
                token: flowId,
                loading,
                sData,
                success(ret) {
                    // miloLocking = false;
                    const iRet = ret.details?.jData?.iRet ?? ret.details?.iRet ?? ret.iRet;
                    const sMsg = ret.details?.jData?.sMsg ?? ret.details?.sMsg ?? ret.sMsg;
                    if (iRet != 0) {
                        console.error('[Act][miloEmit] 请求失败：', { iRet, sMsg }, ret);
                        onFailed?.(ret);
                        resolve(ret);
                        return;
                    }
                    const data = ret.details ?? ret.data ?? ret;
                    console.debug('[Act][miloEmit] 请求成功：', { iRet, sMsg, data }, ret);
                    onSuccess?.(data, ret);
                    resolve(data);
                },
                fail(err) {
                    // miloLocking = false;
                    console.error('[Act][miloEmit] 请求失败：', err);
                    if (err.iRet == 101 || err.sMsg === 'sorry, please log in first!') {
                        // 登录态失效，清除 Cookie
                        console.info('[Act][miloEmit] 接口返回登录态失效，执行登出操作');
                        logout();
                    }
                    onFailed?.(err);
                    resolve(err);
                },
            });
        } catch (err: any) {
            console.error('[Act][miloEmit] Milo.emit 执行异常', err);
            const ret = { iRet: -1, sMsg: err.message ?? err, error: err, data: undefined, details: undefined };
            onFailed?.(ret);
            resolve(ret);
        }
        // 发出请求后，解锁 Milo.emit
        setTimeout(() => (miloLocking = false), 0);
    });
}

/** 拉取活动配置信息 */
export async function getActivityConfig(actName?: string, sData?: { [k: string]: any }, onSuccess?: (data: any) => any, onFailed?: (err: any, ...rest: any[]) => any) {
    if (!actName) actName = (window.$wujie?.location ?? location).pathname.match(/(?:act|cp)\/([^/]+)/)?.[1];
    miloEmit(
        '3000644_X3IFJY', // 公用请求节点，拉取活动配置信息
        'Ku69kx',
        { ...(sData || {}), act: actName }, // act 参数指定需要拉取的活动配置
        (ret) => {
            const data = ret.jData?.jData;
            if (!data) {
                console.warn('[Act] 没有拉取到配置信息:', ret);
                onFailed?.('没有拉取到配置信息', data); // 没有获取到活动配置信息
                return;
            }
            console.info('[Act] 拉取到活动配置信息:', data);
            // 通用下载链接配置信息
            if (data.download) {
                for (const [k, v] of Object.entries(data.download)) downloadUrls[k] = v as string;
                console.info('[Act] 更新下载链接配置:', downloadUrls);
            }
            // 检查活动自定义下载链接配置
            if (data.config) {
                let useActDownload = false;
                for (const [k, v] of Object.entries(data.config)) {
                    const matched = k.match(/^download\.(.+)$/);
                    if (!matched) continue;
                    downloadUrls[matched[1]] = v as string;
                    useActDownload = true;
                }
                if (useActDownload) console.info('[Act] 使用活动自定义下载链接配置:', downloadUrls);
            }
            onSuccess?.(data);
        },
        onFailed, // 拉取活动配置信息失败: 直接初始化页面
    );
}

export function getPrizeState() {}


